/**
 * THE derivations of every pricing FACT the marketing page states in prose.
 *
 * BUG-205, in the reporter's words: "Pricing details are stored separately, so when plan
 * limits or fees change, the FAQ can continue showing outdated information that
 * contradicts the pricing section." It did. The annual discount was typed into six locale
 * strings AND into a JSX literal; the seat count, the per-seat overage, the two
 * data-porting prices and the payment rate were typed into three locale strings each; and
 * `"$129/mo + $1,398 one-time"` was a JSX literal restating the Flow plan's price plus a
 * HAND-COMPUTED sum of two SKU prices. Nine facts, none of them reading the catalogue the
 * same page renders two sections above.
 *
 * So the rule this module exists to enforce: a pricing fact is DERIVED here, ONCE, from
 * the payloads `PlatformLanding` already fetches (`/api/saas/plans`, `/api/saas/skus`,
 * `/api/saas/platform-fees`), and INTERPOLATED into a locale string that contains no
 * number at all. A locale string cannot be kept in sync with a database; a placeholder
 * needs no syncing.
 *
 * EVERY FUNCTION HERE RETURNS `null` WHEN THE FACT IS NOT DERIVABLE, and that is
 * load-bearing rather than defensive: `PlatformLanding` mounts with `plans=[] skus=[]
 * platformFees=[]` and fills them from four awaited GETs, so "no catalogue yet" is the
 * first paint every visitor sees. A caller that gets `null` renders the sentence WITHOUT
 * the figure — never the sentence with a hole, an `undefined` or a stale constant in it.
 *
 * THE MONEY GRIDS ARE READ EXACTLY AS `PlanCard` AND `SkuCard` READ THEM — `plan.prices
 * [currency]`, `sku.price[currency]`, raw key, no normalisation. That is deliberate: the
 * point of this module is that the prose and the cards cannot state different numbers, and
 * a second lookup convention here would be a way for them to.
 *
 * KNOWN, DELIBERATELY NOT FIXED HERE: `annualAmount` mirrors the server's
 * `ANNUAL_MONTHS_PREPAID` fallback (backend/saas_models.py:66,139) because `PlanCard` has
 * always computed it client-side. `/api/saas/plans` publishes `cycle_prices` — the
 * resolved grid produced by the very function that charges the card — precisely so no
 * client re-derives this formula (backend/saas_routes.py:217-225). Migrating the card and
 * this module onto `cycle_prices` is a real repair and a separate one; it changes what the
 * card renders when a plan is not sellable annually, which is not BUG-205's subject.
 */
import { formatPrice } from "./_shared";

/**
 * WHAT THE DEFAULT-FUNNEL PITCH NAMES. These are catalogue IDENTIFIERS, not prices: the
 * pitch is a claim about three specific purchasable rows, and naming them by id is how the
 * amounts stop being hand-copied. `plan_id === "flow"` is already how `PlanCard` decides
 * which card is featured, so the marketing page already depends on this id being stable.
 */
export const FUNNEL_PLAN_ID = "flow";
export const FUNNEL_INSTALL_SKU_ID = "turnkey_install";
export const FUNNEL_PORTING_SKU_ID = "data_port_express";
export const ASSISTED_PORTING_SKU_ID = "data_port_whiteglove";

/** The `/api/saas/platform-fees` row that prices client payments (backend/saas_routes.py:302). */
export const PAY_FEE_ID = "solunary_pay";

/** Months of a year an annual plan prepays when it carries no explicit annual price. */
const ANNUAL_MONTHS_PREPAID = 10;
const MONTHS_PER_YEAR = 12;

/**
 * A money grid cell, or `null`.
 *
 * ZERO IS NOT A PRICE HERE, it is "not sold": `resolve_plan_amount` raises
 * `PlanNotSellable` on a monthly grid entry `<= 0` (backend/saas_models.py:132), and
 * `isSoldIn` in PricingPlans.jsx applies the same rule to decide whether a card is shown
 * at all. A fact quoted from a row the platform would refuse to sell is a fact about
 * nothing. A genuinely free row (`data_port_diy`, priced 0) is therefore never quoted by
 * these derivations, which is correct — "Free" is not a number the prose interpolates.
 */
const amount = (grid, currency) => {
  const value = Number(grid?.[currency]);
  return Number.isFinite(value) && value > 0 ? value : null;
};

const byId = (rows, field, id) => (rows || []).find((r) => r?.[field] === id) || null;

/** What a practice pays per month for `plan` in `currency`, or `null` if not sold in it. */
export const monthlyAmount = (plan, currency) => amount(plan?.prices, currency);

/**
 * What a practice pays per YEAR for `plan` in `currency`, or `null`.
 *
 * `× ANNUAL_MONTHS_PREPAID`, never `× 12`, when no explicit annual price exists — that is
 * what signup actually charges (backend/saas_models.py:139), and the comment this
 * replaced in PricingPlans.jsx recorded what `× 12` cost: the card lied three times over.
 */
export const annualAmount = (plan, currency) => {
  const explicit = amount(plan?.annual_prices, currency);
  if (explicit !== null) return explicit;
  const monthly = monthlyAmount(plan, currency);
  return monthly === null ? null : monthly * ANNUAL_MONTHS_PREPAID;
};

/**
 * The BEST annual saving any plan offers, as a whole percentage, or `null`.
 *
 * "Up to", not "the", and that is the honest shape: `annual_prices` is per plan and per
 * currency and freely editable from super admin, so the plans can and do disagree. One
 * averaged or first-plan figure would be a claim about no plan in particular — the same
 * class of lie as the frozen 17 %. `_fee_summary` (backend/modules/payments/service.py:2683)
 * resolves the identical dilemma the identical way: state one figure only when it is true.
 *
 * `is_custom` plans are excluded: their price is "from", negotiated per customer, so their
 * ratio is not a discount anyone is offered.
 */
export const maxAnnualSavingPercent = (plans, currency) => {
  let best = null;
  for (const plan of plans || []) {
    if (plan?.is_custom) continue;
    const monthly = monthlyAmount(plan, currency);
    const annual = annualAmount(plan, currency);
    if (monthly === null || annual === null) continue;
    const full = monthly * MONTHS_PER_YEAR;
    const percent = Math.round(((full - annual) / full) * 100);
    if (percent > 0 && (best === null || percent > best)) best = percent;
  }
  return best;
};

/**
 * The included-seat facts of the multi-practitioner plan, or `null`.
 *
 * WHICH PLAN: the one that SELLS EXTRA SEATS (`extra_practitioner_price`), lowest `order`
 * first — not a hardcoded "ascend". The overage price is the definition of a
 * multi-practitioner plan, so the plan is identified by the very field whose value is
 * being quoted, and renaming or re-ordering plans in super admin cannot mis-target it.
 *
 * THE SEAT COUNT COMES FROM `feature_rows`, not from `plan.max_practitioners`, even though
 * both are in the payload. `feature_rows` is the resolved, already-localised bullet list
 * the plan CARD renders (backend/entitlements.feature_rows_for_plan, keyed
 * `team.practitioners` — backend/plan_features.py:318). Reading the same row the card
 * reads is what makes "the FAQ and the card agree" structural instead of coincidental;
 * reading the raw field would be a second reader, i.e. a second chance to disagree.
 *
 * An unlimited plan (`value === -1`) yields `null`: "includes -1 practitioners" is not a
 * sentence, and "includes unlimited practitioners, then $40 per extra one" is a
 * contradiction. The caller's number-free variant is the right answer there.
 */
export const teamSeatFacts = (plans, currency) => {
  const candidates = (plans || [])
    .filter((p) => !p?.is_custom && amount(p?.extra_practitioner_price, currency) !== null)
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
  for (const plan of candidates) {
    const row = (plan.feature_rows || []).find(
      (r) => r?.key === "team.practitioners" && r?.kind === "limit",
    );
    const included = Number(row?.value);
    if (!Number.isInteger(included) || included <= 0) continue;
    return {
      plan: plan.name,
      included,
      extra: formatPrice(amount(plan.extra_practitioner_price, currency), currency),
    };
  }
  return null;
};

/** The two data-porting packages the FAQ names, with their prices — or `null`. */
export const portingFacts = (skus, currency) => {
  const express = byId(skus, "sku_id", FUNNEL_PORTING_SKU_ID);
  const assisted = byId(skus, "sku_id", ASSISTED_PORTING_SKU_ID);
  const expressPrice = amount(express?.price, currency);
  const assistedPrice = amount(assisted?.price, currency);
  if (expressPrice === null || assistedPrice === null) return null;
  return {
    express: express.name,
    expressPrice: formatPrice(expressPrice, currency),
    assisted: assisted.name,
    assistedPrice: formatPrice(assistedPrice, currency),
  };
};

/**
 * The advertised rate on client payments, verbatim from the catalogue, or `null`.
 *
 * THE AUTHORITY IS THE ONE THE PRICING SECTION ALREADY RENDERS: `platform/BoostersAndFees.jsx`
 * prints `fee.rate` off this same array, so a FAQ reading it here cannot contradict the
 * fee card — which is precisely what BUG-205 reports. The string is passed through
 * UNPARSED (it is "2.9% + $0.30", "8–12% rev share", "$19/practitioner/mo" depending on the
 * row): re-formatting it on the client would be a second opinion on what the platform
 * charges, and `/api/saas/platform-fees` is the only thing entitled to hold one.
 *
 * SEE THE REPORT accompanying this change for the deeper defect this does NOT fix: that
 * endpoint returns a STATIC literal, while the rate the money core actually charges is
 * effective-dated basis points in `commission_rules`, resolved by
 * `modules/payments/service._fee_label` and published unauthenticated at
 * `GET /api/public/stripe-connect/config`. Those two can disagree, and a percentage-plus-
 * fixed fee like "2.9% + $0.30" is not even expressible in bps by design. Reconciling them
 * is a backend change; what is fixed here is the FAQ having its own THIRD copy.
 */
export const payFeeRate = (platformFees) => {
  const row = byId(platformFees, "id", PAY_FEE_ID);
  const rate = String(row?.rate || "").trim();
  return rate || null;
};

/**
 * The default-funnel bundle — its three row names and its two amounts — or `null`.
 *
 * `null` WHEN ANY ROW IS MISSING, and the caller hides the pitch rather than printing a
 * partial one. The pitch's entire content is "these three specific things, for this total":
 * with one of them deactivated in super admin the total is unknowable AND the bundle is
 * unbuyable, so an incomplete pitch would advertise a package that does not exist. Absent
 * is the honest state, and it is also the first-paint state.
 *
 * The one-time total is a SUM computed here from the two SKU rows. It was `$1,398`, typed.
 */
export const defaultFunnelFacts = (plans, skus, currency) => {
  const plan = byId(plans, "plan_id", FUNNEL_PLAN_ID);
  const install = byId(skus, "sku_id", FUNNEL_INSTALL_SKU_ID);
  const porting = byId(skus, "sku_id", FUNNEL_PORTING_SKU_ID);
  const monthly = monthlyAmount(plan, currency);
  const installPrice = amount(install?.price, currency);
  const portingPrice = amount(porting?.price, currency);
  if (monthly === null || installPrice === null || portingPrice === null) return null;
  return {
    plan: plan.name,
    install: install.name,
    porting: porting.name,
    monthly: formatPrice(monthly, currency),
    oneTime: formatPrice(installPrice + portingPrice, currency),
  };
};
